import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  preferredAliasFor,
  type Person,
  type PersonAlias,
  type PersonProvider,
} from '@zunelo/shared';
import { peopleClient } from '../services/clients';
import type { ComposerMentionCandidate } from '../ui/common/RichTextComposer';

const PROVIDER_LABELS: Record<PersonProvider, string> = {
  slack: 'Slack',
  jira: 'Jira',
  github: 'GitHub',
};

export function mentionAliasLabel(alias: PersonAlias): string {
  const username = alias.username?.trim().replace(/^@/, '');
  return username ? `@${username}` : alias.displayName;
}

export function peopleToMentionCandidates(
  people: readonly Person[],
  platform: PersonProvider,
): ComposerMentionCandidate[] {
  return people.map((person) => {
    const alias = preferredAliasFor(person, platform);
    if (!alias) {
      return {
        id: `person:${person.id}`,
        label: person.displayName,
        platform,
        disabled: true,
        disabledReason: `No hay una cuenta de ${PROVIDER_LABELS[platform]} confirmada para esta persona.`,
      };
    }
    return {
      id: `person:${person.id}`,
      label: person.displayName,
      aliasLabel: mentionAliasLabel(alias),
      personId: person.id,
      aliasId: alias.id,
      platform,
    };
  });
}

export function invalidatePeopleMentionCandidates(): void {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event('zunelo:people-changed'));
  }
}

export interface PeopleMentionCandidatesState {
  candidates: ComposerMentionCandidate[];
  loading: boolean;
  error: string | null;
}

export function usePeopleMentionCandidates(
  platform: PersonProvider,
  enabled = true,
): PeopleMentionCandidatesState {
  const [people, setPeople] = useState<Person[]>([]);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState<string | null>(null);
  const mountedRef = useRef(true);

  const load = useCallback(async () => {
    if (!mountedRef.current) return;
    setLoading(true);
    try {
      const result = await peopleClient.listPeople();
      if (!mountedRef.current) return;
      setPeople(result.data);
      setError(null);
    } catch (cause) {
      if (!mountedRef.current) return;
      setError(cause instanceof Error ? cause.message : 'No se pudieron cargar las personas.');
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return undefined;
    }
    mountedRef.current = true;
    void load();
    const onPeopleChanged = () => void load();
    window.addEventListener('zunelo:people-changed', onPeopleChanged);
    return () => {
      mountedRef.current = false;
      window.removeEventListener('zunelo:people-changed', onPeopleChanged);
    };
  }, [enabled, load]);

  const candidates = useMemo(() => peopleToMentionCandidates(people, platform), [people, platform]);
  return { candidates, loading, error };
}
